<div class="flex flex-col w-full h-full bg-white overflow-hidden">
  @if (loading) {
    <app-loader></app-loader>
  }

  <div class="flex justify-between items-start p-6 border-b border-gray-200">
    <div class="flex items-center gap-4 min-w-0">
      <div
        class="w-16 h-16 rounded-2xl bg-purple-50 text-purple-700 overflow-hidden flex items-center justify-center shrink-0"
      >
        <span class="text-lg font-semibold">
          {{ serviceForm.get("name")?.value?.charAt(0) || "S" }}
        </span>
      </div>

      <div class="flex flex-col min-w-0">
        <span
          class="text-xs text-purple-700 bg-purple-50 px-3 py-1 rounded-full w-fit mb-2"
        >
          Salon Service
        </span>

        <h2 class="text-xl md:text-2xl font-semibold text-gray-900 truncate">
          {{ serviceForm.get("name")?.value || modalTitle }}
        </h2>

        <p class="text-sm text-gray-600 truncate">
          {{ selectedSalonName || "No salon selected yet" }}
        </p>
      </div>
    </div>

    <button
      type="button"
      class="w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-50"
      (click)="onClose()"
    >
      ×
    </button>
  </div>

  <div
    class="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-gray-200"
  >
    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Service Status</span>
      <strong class="text-lg font-semibold text-gray-900">
        {{ isEditMode ? "Existing" : "New" }}
      </strong>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Price</span>
      <strong class="text-lg font-semibold text-gray-900">
        {{ toMoney(serviceForm.get("price")?.value) }}
      </strong>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Duration</span>
      <strong class="text-lg font-semibold text-gray-900">
        {{ serviceForm.get("duration_minutes")?.value || 0 }} mins
      </strong>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Salon</span>
      <strong class="text-lg font-semibold text-gray-900 truncate block">
        {{ selectedSalonName || "Pending" }}
      </strong>
    </div>
  </div>

  <div class="flex gap-2 overflow-x-auto p-4 border-b border-gray-200 bg-white">
    <button
      *ngFor="let tab of tabs"
      type="button"
      class="px-4 py-2 rounded-3xl border text-sm whitespace-nowrap"
      [class.bg-gray-950]="activeTab === tab.key"
      [class.text-white]="activeTab === tab.key"
      [class.border-gray-950]="activeTab === tab.key"
      [class.bg-white]="activeTab !== tab.key"
      [class.text-gray-700]="activeTab !== tab.key"
      [class.border-gray-200]="activeTab !== tab.key"
      (click)="activeTab = tab.key"
    >
      {{ tab.label }}
    </button>
  </div>

  <form
    class="flex-1 overflow-y-auto p-6 flex flex-col gap-6"
    [formGroup]="serviceForm"
    (ngSubmit)="onSubmit()"
  >
    @if (activeTab === "overview") {
      <section class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="bg-white border border-gray-200 rounded-lg p-5">
          <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
            Service Details
          </h3>

          <div class="grid gap-4">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Service Name *
              </span>

              <input
                type="text"
                class="input"
                placeholder="Haircut, Facial, Manicure..."
                formControlName="name"
                [class.!border-red-500]="!checkValidField('name')"
              />

              <span class="error" *ngIf="!checkValidField('name')">
                Service name is required
              </span>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Assigned Salon *
              </span>

              <select
                class="input"
                formControlName="salon_id"
                [class.!border-red-500]="!checkValidField('salon_id')"
              >
                <option value="">Choose Salon</option>
                @for (salon of salons; track salon.salon_id) {
                  <option [value]="salon.salon_id">
                    {{ salon.name }}
                  </option>
                }
              </select>

              <span class="error" *ngIf="!checkValidField('salon_id')">
                Salon is required
              </span>
            </label>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-lg p-5">
          <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
            Pricing & Duration
          </h3>

          <div class="grid gap-4">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">Price *</span>

              <div class="relative">
                <span
                  class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  $
                </span>

                <input
                  type="number"
                  class="input pl-7"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  formControlName="price"
                  [class.!border-red-500]="!checkValidField('price')"
                />
              </div>

              <span class="error" *ngIf="!checkValidField('price')">
                Price must be greater than 0
              </span>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Duration Minutes *
              </span>

              <input
                type="number"
                class="input"
                placeholder="30"
                min="5"
                step="5"
                formControlName="duration_minutes"
                [class.!border-red-500]="!checkValidField('duration_minutes')"
              />

              <span class="error" *ngIf="!checkValidField('duration_minutes')">
                Duration must be at least 5 minutes
              </span>
            </label>
          </div>
        </div>
      </section>
    }

    @if (activeTab === "notes") {
      <section class="bg-white border border-gray-200 rounded-lg p-5">
        <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
          Internal Notes
        </h3>

        <textarea
          rows="12"
          class="input min-h-[240px]"
          placeholder="Optional notes about this service, booking guidance, add-ons, staff instructions..."
          formControlName="description"
        ></textarea>
      </section>
    }

    <div class="sticky bottom-0 bg-white pt-4 border-t border-gray-200">
      <div class="ml-auto flex justify-end gap-3">
        <button
          type="button"
          class="px-6 py-2 shadow rounded-3xl bg-white text-gray-900 border border-gray-200"
          (click)="onClose()"
        >
          Cancel
        </button>

        <button
          type="submit"
          class="px-6 py-2 shadow rounded-3xl bg-gray-950 text-white"
        >
          {{ isEditMode ? "Save Changes" : "Create Service" }}
        </button>
      </div>
    </div>
  </form>
</div>